import jsbridge from "@xpeng/js-bridge";

const VOICE_BRIDGE_TIMEOUT_MS = 2_000;
const VOICE_PERMISSION_TIMEOUT_MS = 30_000;

const RECORD_STATUS = {
  start: 0,
  end: 1,
  cancel: 2,
  initASR: 3,
};

export interface VoiceRecordingResult {
  code?: number;
  requestId?: string;
  isPermissionGranted?: boolean;
}

export interface VoiceRecognitionResult {
  code?: number;
  result?: string;
}

interface PermissionResult {
  data?: {
    isGranted?: boolean;
  };
}

export class VoiceBridgeTimeoutError extends Error {
  constructor(action: string) {
    super(`Voice bridge timed out: ${action}`);
    this.name = "VoiceBridgeTimeoutError";
  }
}

/**
 * 原生语音调用必须在有限时间内结束，避免电话等音频中断后回调丢失，
 * 导致 H5 的录音状态和全局语音队列永久等待。
 */
function callVoiceBridgeWithTimeout<T>(
  handlerName: string,
  params: Record<string, unknown>,
  action: string,
  timeoutMs = VOICE_BRIDGE_TIMEOUT_MS
) {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      settled = true;
      reject(new VoiceBridgeTimeoutError(action));
    }, timeoutMs);

    Promise.resolve()
      .then(() => jsbridge.callHandler(handlerName, params))
      .then(
        (result) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(result as T);
        },
        (error) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(error);
        }
      );
  });
}

/** 初始化原生 ASR 能力并返回当前麦克风权限状态。 */
export async function initializeVoiceToText() {
  const result = await callVoiceBridgeWithTimeout<VoiceRecordingResult>(
    "recordAudio",
    { status: RECORD_STATUS.initASR },
    "initASR"
  );
  return !!result?.isPermissionGranted;
}

/** 请求原生麦克风权限。等待用户操作，但仍设置有限超时避免永久阻塞。 */
export async function requestMicrophonePermission() {
  const result = await callVoiceBridgeWithTimeout<PermissionResult>(
    "requestPermission",
    { permission: "mic" },
    "requestPermission",
    VOICE_PERMISSION_TIMEOUT_MS
  );
  return !!result?.data?.isGranted;
}

/** 开始原生录音并返回本次识别请求标识。 */
export function startVoiceRecording() {
  return callVoiceBridgeWithTimeout<VoiceRecordingResult>(
    "recordAudio",
    { status: RECORD_STATUS.start },
    "start"
  );
}

/** 结束当前原生录音。 */
export function stopVoiceRecording() {
  return callVoiceBridgeWithTimeout<VoiceRecordingResult>(
    "recordAudio",
    { status: RECORD_STATUS.end },
    "stop"
  );
}

/** 取消当前原生录音。 */
export function cancelVoiceRecording() {
  return callVoiceBridgeWithTimeout<VoiceRecordingResult>(
    "recordAudio",
    { status: RECORD_STATUS.cancel },
    "cancel"
  );
}

/** 获取指定录音请求的 ASR 识别结果。 */
export function getVoiceRecognitionResult(requestId: string) {
  return callVoiceBridgeWithTimeout<VoiceRecognitionResult>(
    "getAsrResult",
    { requestId },
    "getAsrResult"
  );
}
